import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../context/ThemeContext';
import Sidebar from '../components/Sidebar';
import api from '../services/api';
import '../styles/theme.css';

const SMS_TRIGGERS = [
  { key: 'new_job_matches',     label: 'New Job Matches',       desc: 'When new jobs match your CV',                icon: 'fa-bullseye',        tier: 'pro' },
  { key: 'application_updates', label: 'Application Updates',   desc: 'When a recruiter views or updates your application', icon: 'fa-paper-plane', tier: 'pro' },
  { key: 'interview_reminders', label: 'Interview Invitations', desc: 'When you receive an interview invite',        icon: 'fa-calendar-check',  tier: 'pro' },
  { key: 'auto_apply_summary',  label: 'Auto-Apply Summary',    desc: 'Daily summary of auto-applied jobs',         icon: 'fa-bolt',            tier: 'pro' },
];

const EMAIL_TRIGGERS = [
  { key: 'new_job_matches',     label: 'New Job Matches',       desc: 'Email when new jobs match your CV',          icon: 'fa-bullseye' },
  { key: 'application_updates', label: 'Application Updates',   desc: 'Email on application status changes',        icon: 'fa-paper-plane' },
  { key: 'interview_reminders', label: 'Interview Reminders',   desc: 'Email reminders for upcoming interviews',    icon: 'fa-calendar-check' },
  { key: 'auto_apply_summary',  label: 'Auto-Apply Summary',    desc: 'Daily email summary of auto-applied jobs',   icon: 'fa-bolt' },
  { key: 'weekly_digest',       label: 'Weekly Digest',         desc: 'Weekly roundup of your job search activity', icon: 'fa-newspaper' },
  { key: 'marketing_emails',    label: 'Tips & Updates',        desc: 'Product updates and career tips',            icon: 'fa-envelope-open-text' },
];

export default function NotificationSettings() {
  const navigate = useNavigate();
  const { isDark, toggleTheme } = useTheme();
  const [prefs, setPrefs] = useState(null);
  const [phone, setPhone] = useState('');
  const [saving, setSaving] = useState(false);
  const [savingPhone, setSavingPhone] = useState(false);
  const [msg, setMsg] = useState({ text: '', type: '' });
  const [loading, setLoading] = useState(true);
  const tier = localStorage.getItem('user_tier') || 'free';
  const hasSMS = tier === 'pro' || tier === 'premium';

  useEffect(() => {
    Promise.all([
      api.get('/email-preferences/').catch(() => null),
      api.get('/job-seeker-profile/my-profile/').catch(() => null),
    ]).then(([prefsRes, profileRes]) => {
      if (prefsRes?.data) setPrefs(prefsRes.data);
      else setPrefs({ new_job_matches: true, application_updates: true, interview_reminders: true, auto_apply_summary: true, weekly_digest: true, marketing_emails: false });
      if (profileRes?.data?.phone) setPhone(profileRes.data.phone);
    }).finally(() => setLoading(false));
  }, []);

  const showMsg = (text, type = 'success') => {
    setMsg({ text, type });
    setTimeout(() => setMsg({ text: '', type: '' }), 4000);
  };

  const togglePref = (key) => {
    setPrefs(p => ({ ...p, [key]: !p[key] }));
  };

  const savePrefs = async () => {
    setSaving(true);
    try {
      await api.patch('/email-preferences/', prefs);
      showMsg('Notification preferences saved.');
    } catch (e) {
      showMsg(e.response?.data?.detail || 'Failed to save preferences.', 'error');
    } finally { setSaving(false); }
  };

  const savePhone = async () => {
    setSavingPhone(true);
    try {
      await api.patch('/job-seeker-profile/my-profile/', { phone });
      showMsg('Phone number saved. SMS alerts will be sent to this number.');
    } catch (e) {
      showMsg(e.response?.data?.detail || 'Failed to save phone number.', 'error');
    } finally { setSavingPhone(false); }
  };

  if (loading) return (
    <div className="portal-layout"><Sidebar />
      <main className="portal-main"><div className="portal-content">
        <div className="empty-state"><i className="fas fa-spinner fa-spin" style={{ opacity: 1, color: 'var(--primary)' }}></i><p>Loading...</p></div>
      </div></main>
    </div>
  );

  return (
    <div className="portal-layout">
      <Sidebar />
      <main className="portal-main">
        <div className="portal-topbar">
          <span className="topbar-title">Notification Settings</span>
          <div className="topbar-actions">
            <button className="topbar-btn" onClick={toggleTheme}>
              <i className={`fas ${isDark ? 'fa-sun' : 'fa-moon'}`}></i>
            </button>
          </div>
        </div>

        <div className="portal-content">
          <div className="page-header">
            <h1>Notification Settings</h1>
            <p>Control how and when JobLinked AI contacts you</p>
          </div>

          {msg.text && (
            <div className={`alert alert-${msg.type === 'error' ? 'error' : 'success'}`}>
              <i className={`fas ${msg.type === 'error' ? 'fa-exclamation-circle' : 'fa-check-circle'}`}></i>
              {msg.text}
            </div>
          )}

          {/* SMS Section */}
          <div className="card" style={{ marginBottom: '1.5rem' }}>
            <div className="card-header">
              <span className="card-title">
                <i className="fas fa-sms" style={{ color: 'var(--primary)', marginRight: '0.5rem' }}></i>
                SMS Alerts
                <span className="badge badge-blue" style={{ marginLeft: '0.5rem', fontSize: '0.7rem' }}>Pro & Premium</span>
              </span>
              {hasSMS
                ? <span className="badge badge-green"><i className="fas fa-check"></i> Active</span>
                : <span className="badge badge-gray">Locked</span>}
            </div>

            {!hasSMS ? (
              <div style={{ padding: '1.5rem 0 0.5rem', textAlign: 'center' }}>
                <i className="fas fa-lock" style={{ fontSize: '2rem', color: 'var(--text-muted)', marginBottom: '0.75rem', display: 'block' }} />
                <p style={{ color: 'var(--text-muted)', marginBottom: '1rem' }}>
                  SMS alerts are available on the Pro plan (R299/month) and above.
                </p>
                <button className="btn btn-primary btn-sm" onClick={() => navigate('/pricing')}>
                  <i className="fas fa-crown"></i> Upgrade to Pro
                </button>
              </div>
            ) : (
              <>
                {/* Phone number input */}
                <div style={{ marginBottom: '1.25rem' }}>
                  <label className="form-label">Your SA Mobile Number</label>
                  <div style={{ display: 'flex', gap: '0.75rem' }}>
                    <input
                      className="form-control"
                      placeholder="e.g. 0821234567 or +27821234567"
                      value={phone}
                      onChange={e => setPhone(e.target.value)}
                      style={{ flex: 1 }}
                    />
                    <button className="btn btn-primary" onClick={savePhone} disabled={savingPhone}>
                      <i className={`fas ${savingPhone ? 'fa-spinner fa-spin' : 'fa-save'}`}></i>
                      {savingPhone ? 'Saving...' : 'Save'}
                    </button>
                  </div>
                  <div className="form-hint">SA numbers only. Format: 0821234567 or +27821234567</div>
                </div>

                {/* SMS toggles */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                  {SMS_TRIGGERS.map(t => (
                    <div key={t.key} style={{
                      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                      padding: '0.875rem 1rem', borderRadius: 8, background: 'var(--surface-2)',
                      border: '1px solid var(--border)',
                    }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                        <div className="stat-icon-wrap blue" style={{ width: 36, height: 36, fontSize: '0.85rem' }}>
                          <i className={`fas ${t.icon}`}></i>
                        </div>
                        <div>
                          <div style={{ fontWeight: 600, fontSize: '0.875rem', color: 'var(--text-primary)' }}>{t.label}</div>
                          <div style={{ fontSize: '0.78rem', color: 'var(--text-muted)' }}>{t.desc}</div>
                        </div>
                      </div>
                      <label style={{ position: 'relative', display: 'inline-block', width: 44, height: 24, flexShrink: 0 }}>
                        <input type="checkbox" checked={!!prefs?.[t.key]} onChange={() => togglePref(t.key)}
                          style={{ opacity: 0, width: 0, height: 0 }} />
                        <span style={{
                          position: 'absolute', cursor: 'pointer', inset: 0, borderRadius: 24,
                          background: prefs?.[t.key] ? 'var(--primary)' : 'var(--border)',
                          transition: '0.2s',
                        }}>
                          <span style={{
                            position: 'absolute', height: 18, width: 18, left: prefs?.[t.key] ? 23 : 3,
                            bottom: 3, background: '#fff', borderRadius: '50%', transition: '0.2s',
                          }} />
                        </span>
                      </label>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>

          {/* Email Section */}
          <div className="card" style={{ marginBottom: '1.5rem' }}>
            <div className="card-header">
              <span className="card-title">
                <i className="fas fa-envelope" style={{ color: 'var(--primary)', marginRight: '0.5rem' }}></i>
                Email Notifications
              </span>
              <span className="badge badge-green"><i className="fas fa-check"></i> All Plans</span>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem', paddingTop: '0.5rem' }}>
              {EMAIL_TRIGGERS.map(t => (
                <div key={t.key} style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  padding: '0.875rem 1rem', borderRadius: 8, background: 'var(--surface-2)',
                  border: '1px solid var(--border)',
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                    <div className="stat-icon-wrap purple" style={{ width: 36, height: 36, fontSize: '0.85rem' }}>
                      <i className={`fas ${t.icon}`}></i>
                    </div>
                    <div>
                      <div style={{ fontWeight: 600, fontSize: '0.875rem', color: 'var(--text-primary)' }}>{t.label}</div>
                      <div style={{ fontSize: '0.78rem', color: 'var(--text-muted)' }}>{t.desc}</div>
                    </div>
                  </div>
                  <label style={{ position: 'relative', display: 'inline-block', width: 44, height: 24, flexShrink: 0 }}>
                    <input type="checkbox" checked={!!prefs?.[t.key]} onChange={() => togglePref(t.key)}
                      style={{ opacity: 0, width: 0, height: 0 }} />
                    <span style={{
                      position: 'absolute', cursor: 'pointer', inset: 0, borderRadius: 24,
                      background: prefs?.[t.key] ? 'var(--primary)' : 'var(--border)',
                      transition: '0.2s',
                    }}>
                      <span style={{
                        position: 'absolute', height: 18, width: 18, left: prefs?.[t.key] ? 23 : 3,
                        bottom: 3, background: '#fff', borderRadius: '50%', transition: '0.2s',
                      }} />
                    </span>
                  </label>
                </div>
              ))}
            </div>
          </div>

          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.75rem' }}>
            <button className="btn btn-secondary" onClick={() => navigate('/dashboard')}>Cancel</button>
            <button className="btn btn-primary" onClick={savePrefs} disabled={saving}>
              <i className={`fas ${saving ? 'fa-spinner fa-spin' : 'fa-save'}`}></i>
              {saving ? 'Saving...' : 'Save Preferences'}
            </button>
          </div>
        </div>
      </main>
    </div>
  );
}
